import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Bell } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Bell icon with a red-dot pulse for admins & sellers when NEW orders land
 * since the last time they visited the orders dashboard. Polls every 30s.
 * Hidden entirely for customers (they get no notifications yet).
 *
 * Plays a synthesized "cha-ching" (two-tone bell) via WebAudio the FIRST
 * time count jumps up while the tab is focused — no MP3 asset needed and
 * respects browsers' autoplay gates (only fires after the user has
 * interacted with the page at least once). Silence via localStorage
 * flag `orders-mute=1`.
 */

function playChaChing() {
  try {
    if (localStorage.getItem("orders-mute") === "1") return;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    if (ctx.state === "suspended") { ctx.resume(); }
    const now = ctx.currentTime;
    // Two chimes ~120ms apart — classic cash-register bell.
    [
      { at: 0, freq: 1760 },       // A6
      { at: 0.12, freq: 2637 },    // E7
    ].forEach(({ at, freq }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.value = freq;
      osc.connect(gain);
      gain.connect(ctx.destination);
      gain.gain.setValueAtTime(0.0001, now + at);
      gain.gain.exponentialRampToValueAtTime(0.22, now + at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + at + 0.35);
      osc.start(now + at);
      osc.stop(now + at + 0.4);
    });
    // Auto-close context after 1s to avoid leak.
    setTimeout(() => { try { ctx.close(); } catch { /* ignore */ } }, 1000);
  } catch { /* audio blocked or unsupported — silent no-op */ }
}

export default function OrderNotificationBell() {
  const { user } = useAuth();
  const [count, setCount] = useState(0);
  // Ref of last count we've seen — so we only play the sound on *increase*,
  // not on initial mount or after mark-seen.
  const prevCountRef = useRef(null);
  const shouldShow = user && (user.role === "admin" || user.role === "seller");

  const refresh = useCallback(async () => {
    if (!shouldShow) return;
    try {
      const { data } = await api.get("/notifications/unread");
      const next = data.unread || 0;
      const prev = prevCountRef.current;
      if (prev !== null && next > prev) playChaChing();
      prevCountRef.current = next;
      setCount(next);
    } catch { /* fail silent — no-op */ }
  }, [shouldShow]);

  useEffect(() => {
    if (!shouldShow) return;
    refresh();
    const id = setInterval(refresh, 30000);
    // Refresh on tab focus so admins see the latest immediately when they return.
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    // Refresh when the mark-seen event fires from the orders page.
    const onMarked = () => { setCount(0); prevCountRef.current = 0; };
    window.addEventListener("orders-marked-seen", onMarked);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("orders-marked-seen", onMarked);
    };
  }, [refresh, shouldShow]);

  if (!shouldShow) return null;

  const target = user.role === "admin" ? "/admin/orders" : "/seller/orders";

  return (
    <Link
      to={target}
      data-testid="order-notification-bell"
      aria-label={count ? `${count} new orders` : "Orders"}
      className="relative inline-flex items-center justify-center w-10 h-10 hover:bg-neutral-100 border border-transparent hover:border-black transition-colors"
    >
      <Bell className="w-5 h-5" />
      {count > 0 && (
        <span
          data-testid="order-notification-count"
          className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-[#FF2A00] text-white text-[10px] font-black leading-[18px] rounded-full text-center shadow-[1px_1px_0_0_#000] animate-pulse"
        >
          {count > 99 ? "99+" : count}
        </span>
      )}
    </Link>
  );
}
